import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  Clock, ArrowLeft,
  Facebook, Twitter, Linkedin, ChevronRight, Tag
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import Header from './Header';
import Footer from './Footer';
import { blogPostsData } from '../data/blogData';
import { useAppointment } from '../context/AppointmentContext';
import usePageSeo from '../hooks/usePageSeo';
import useJsonLd from '../hooks/useJsonLd';

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { openAppointmentModal } = useAppointment();

  const post = blogPostsData.find(p => p.slug === slug);

  // Hooks must be called unconditionally before any early return
  const postCanonical = post ? `https://www.atlregen.com/blog/${post.slug}` : null;
  const postOgImage = post?.image?.startsWith('/')
    ? `https://www.atlregen.com${post.image}`
    : (post?.image || null);

  usePageSeo({
    title: post ? `${post.seoTitle || post.title} | ATL Regen` : 'Article Not Found | ATL Regen',
    description: post ? post.excerpt.slice(0, 155) : null,
    canonical: postCanonical,
    ogTitle: post ? post.title : null,
    ogDescription: post ? post.excerpt.slice(0, 155) : null,
    ogImage: postOgImage,
  });

  useJsonLd(post ? {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.excerpt,
    "image": postOgImage || 'https://www.atlregen.com/images/social/og-image.png',
    "author": { "@type": "Person", "name": post.author },
    "publisher": { "@type": "Organization", "name": "ATL Regen", "url": "https://www.atlregen.com/" },
    "datePublished": post.date,
    "url": `https://www.atlregen.com/blog/${post.slug}`,
    "mainEntityOfPage": { "@type": "WebPage", "@id": `https://www.atlregen.com/blog/${post.slug}` }
  } : null);

  // Early return for not found - MUST be before any JSX that uses post properties
  if (!post) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="container mx-auto px-4 py-20 text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Article Not Found</h1>
          <p className="text-gray-600 mb-8">The article you're looking for doesn't exist.</p>
          <Button onClick={() => navigate('/blog')} className="bg-[#E63946] hover:bg-[#D32F3D]">
            Back to Blog
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  const relatedPosts = blogPostsData
    .filter(p => p.id !== post.id && p.category === post.category)
    .slice(0, 2);

  const pageUrl = encodeURIComponent(
    typeof window !== 'undefined'
      ? window.location.href
      : `https://www.atlregen.com/blog/${post.slug}`
  );
  const shareText = encodeURIComponent(post.title);
  const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${pageUrl}`;
  const twitterShareUrl = `https://x.com/intent/tweet?url=${pageUrl}&text=${shareText}`;
  const linkedinShareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${pageUrl}`;

  const consultationHeading = '## **Schedule a Consultation with ATL Regen**';
  const hasConsultationSection = post.id === 1 && post.content.includes(consultationHeading);

  // Parse markdown-like content to HTML
  const parseContent = (content) => {
    const allLines = content.split('\n').map((line) => line.trim());
    const consultationIndex = allLines.findIndex(
      (line) =>
        line === consultationHeading ||
        line === '## Schedule a Consultation with ATL Regen' ||
        line === '## **Schedule a Consultation with ATL Regen**'
    );
    const lines = consultationIndex >= 0 ? allLines.slice(0, consultationIndex) : allLines;
    const html = [];
    let inList = false;

    const formatInline = (text) =>
      text.replace(/\*\*(.*?)\*\*/g, '<strong class="text-[#1B1B3A]">$1</strong>');

    const closeList = () => {
      if (inList) {
        html.push('</ul>');
        inList = false;
      }
    };

    lines.forEach((line) => {
      if (!line) {
        closeList();
        return;
      }

      if (line.startsWith('### ')) {
        closeList();
        html.push(
          `<h3 class="text-xl font-semibold text-[#1B1B3A] mt-6 mb-3">${formatInline(line.slice(4))}</h3>`
        );
        return;
      }

      if (line.startsWith('## ')) {
        closeList();
        html.push(
          `<h2 class="text-2xl font-bold text-[#1B1B3A] mt-8 mb-4">${formatInline(line.slice(3))}</h2>`
        );
        return;
      }

      if (line.startsWith('- ')) {
        if (!inList) {
          html.push('<ul class="list-disc pl-6 mb-4 space-y-1">');
          inList = true;
        }
        html.push(`<li class="text-gray-600">${formatInline(line.slice(2))}</li>`);
        return;
      }

      closeList();
      html.push(`<p class="text-gray-600 leading-relaxed mb-4">${formatInline(line)}</p>`);
    });

    closeList();
    return html.join('');
  };

  return (
    <div className="min-h-screen bg-white">
      
      <Header />
      
      <main>
        {/* Hero Image */}
        <div className="relative h-[300px] md:h-[400px] lg:h-[500px] overflow-hidden">
          <img
            src={post.image}
            alt={post.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1B1B3A] via-[#1B1B3A]/50 to-transparent"></div>
          
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 lg:p-12">
            <div className="container mx-auto max-w-4xl">
              <Link 
                to="/blog"
                className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Blog
              </Link>
              
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="bg-[#20B2AA] text-white text-sm font-semibold px-4 py-1 rounded-full">
                  {post.category}
                </span>
                <span className="text-white/80 text-sm flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  {post.readTime}
                </span>
              </div>
              
              <h1 className="text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-white mb-4 leading-tight">
                {post.title}
              </h1>
              
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-3">
                  <div className="bg-[#E63946] text-white w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm">
                    {post.author.split(' ').filter(n => !n.endsWith('.')).map(n => n[0]).join('')}
                  </div>
                  <div>
                    <div className="font-semibold text-white text-sm md:text-base">{post.author}</div>
                    <div className="text-sm text-white/70">{post.date}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Article Content */}
        <article className="py-8 md:py-12 lg:py-16">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-4 gap-8 lg:gap-12 max-w-6xl mx-auto">
              {/* Main Content */}
              <div className="lg:col-span-3">
                <div 
                  className="prose prose-lg max-w-none"
                  dangerouslySetInnerHTML={{ __html: parseContent(post.content) }}
                />

                {hasConsultationSection && (
                  <Card className="mt-10 border-none shadow-xl bg-gradient-to-br from-[#1B1B3A] to-[#2D2D4A] text-white">
                    <CardContent className="p-6 md:p-8">
                      <h2 className="text-2xl font-bold mb-4">Schedule a Consultation with ATL Regen</h2>
                      <p className="text-gray-200 mb-4">
                        If you are dealing with chronic back pain, neck pain, joint pain, or pain after an auto accident, ATL Regen may be able to help you explore non-surgical treatment options.
                      </p>
                      <p className="text-gray-200 mb-6">
                        Schedule a consultation with ATL Regen to find out whether regenerative medicine, PRP injections, or image-guided interventional treatment may be appropriate for your condition.
                      </p>
                      <Button
                        className="bg-[#E63946] hover:bg-[#D32F3D]"
                        onClick={openAppointmentModal}
                        data-testid="blog-post-inline-free-consultation-btn"
                      >
                        Book an Appointment
                      </Button>
                      <p className="text-sm text-gray-300 mt-4">
                        Results vary by patient. Treatment recommendations depend on diagnosis, imaging findings, and clinical evaluation. Some regenerative therapies may not be FDA-approved for every indication discussed.
                      </p>
                    </CardContent>
                  </Card>
                )}

                {/* Share Section */}
                <div className="border-t border-gray-200 mt-12 pt-8">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                      <Tag className="h-5 w-5 text-gray-400" />
                      <span className="text-gray-600">Category:</span>
                      <Link 
                        to="/blog"
                        className="text-[#E63946] font-semibold hover:underline"
                      >
                        {post.category}
                      </Link>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <span className="text-gray-600">Share:</span>
                      <a
                        href={facebookShareUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-[#1877F2] text-white p-2 rounded-full hover:opacity-80 transition-opacity"
                        aria-label="Facebook"
                      >
                        <Facebook className="h-4 w-4" />
                      </a>
                      <a
                        href={twitterShareUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-[#1DA1F2] text-white p-2 rounded-full hover:opacity-80 transition-opacity"
                        aria-label="Twitter"
                      >
                        <Twitter className="h-4 w-4" />
                      </a>
                      <a
                        href={linkedinShareUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-[#0A66C2] text-white p-2 rounded-full hover:opacity-80 transition-opacity"
                        aria-label="LinkedIn"
                      >
                        <Linkedin className="h-4 w-4" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sidebar */}
              <div className="lg:col-span-1">
                <div className="sticky top-32 space-y-8">
                  {/* CTA Card */}
                  <Card className="border-none shadow-xl bg-gradient-to-br from-[#1B1B3A] to-[#2D2D4A] text-white">
                    <CardContent className="p-6">
                      <h3 className="font-bold text-lg mb-3">Ready to Get Started?</h3>
                      <p className="text-sm text-gray-300 mb-4">
                        Book an appointment to discuss your treatment options.
                      </p>
                      <Button
                        className="w-full bg-[#E63946] hover:bg-[#D32F3D]"
                        onClick={openAppointmentModal}
                        data-testid="blog-post-free-consultation-btn"
                      >
                        Book an Appointment
                      </Button>
                      <p className="text-center text-sm text-gray-400 mt-3">
                        or call <a href="tel:+14043552728" className="text-[#E63946] hover:underline">(404) 355-2728</a>
                      </p>
                    </CardContent>
                  </Card>

                  {/* Related Posts */}
                  {relatedPosts.length > 0 && (
                    <div>
                      <h3 className="font-bold text-lg text-gray-900 mb-4">Related Articles</h3>
                      <div className="space-y-4">
                        {relatedPosts.map((relatedPost) => (
                          <Link 
                            key={relatedPost.id}
                            to={`/blog/${relatedPost.slug}`}
                            className="block group"
                          >
                            <Card className="border-gray-200 hover:border-[#E63946] transition-colors overflow-hidden">
                              <div className="aspect-video overflow-hidden">
                                <img
                                  src={relatedPost.image}
                                  alt={relatedPost.title}
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                />
                              </div>
                              <CardContent className="p-4">
                                <h4 className="font-semibold text-gray-900 group-hover:text-[#E63946] transition-colors line-clamp-2 text-sm">
                                  {relatedPost.title}
                                </h4>
                                <span className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                                  <Clock className="h-3 w-3" />
                                  {relatedPost.readTime}
                                </span>
                              </CardContent>
                            </Card>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </article>

        {/* More Articles CTA */}
        <section className="py-12 bg-gray-50">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Explore More Health Insights
            </h2>
            <Link to="/blog">
              <Button variant="outline" size="lg" className="border-2 border-[#1B1B3A] text-[#1B1B3A] hover:bg-[#1B1B3A] hover:text-white">
                View All Articles
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default BlogPost;
